'use client';

import { CssBaseline } from '@mui/material';
import { AppRouterCacheProvider } from '@mui/material-nextjs/v16-appRouter';
import { createTheme, ThemeProvider } from '@mui/material/styles';
import PropTypes from 'prop-types';
import { useEffect, useMemo } from 'react';

import { useThemeStore } from '@/store/useThemeStore';

/**
 * Fixed brand colors (identical in light and dark themes, like `lumoraGreenPrimary`). These are the
 * literal brand hues; surfaces that need a different treatment on dark backgrounds switch at the
 * call site (e.g. `theme.palette.mode === 'dark' ? text.primary : theme.palette.brand.greenDark`).
 * Exported as a plain constant too, for consumers outside the MUI theme context (e.g. the app
 * shell passing colors to LumoraWrapper).
 */
export const BRAND = {
	// Darkened brand green — the always-dark sidebar surface, active scope tab + headings in light mode.
	greenDark: '#072d29',
	// Deep navy ink — secondary heading / description text in light mode.
	ink: '#05223e'
};

const headingStyle = {
	fontFamily: 'var(--font-montserrat)',
	fontWeight: 600,
	lineHeight: 1,
	letterSpacing: '0.02em'
};

const typography = {
	fontFamily: 'var(--font-montserrat)',
	h1: headingStyle,
	h2: headingStyle,
	h3: headingStyle,
	h4: { ...headingStyle, fontSize: '32px' },
	h5: { ...headingStyle, fontSize: '18px' },
	h6: headingStyle,
	body1: { fontFamily: 'var(--font-source-sans-3)' },
	body2: { fontFamily: 'var(--font-source-sans-3)' },
	subtitle1: { fontFamily: 'var(--font-source-sans-3)' },
	subtitle2: { fontFamily: 'var(--font-source-sans-3)' },
	caption: { fontFamily: 'var(--font-source-sans-3)' },
	overline: { fontFamily: 'var(--font-source-sans-3)' },
	button: { fontFamily: 'var(--font-source-sans-3)' }
};

// Brand-green highlight for menu/list options (Select MenuItems + Autocomplete options), tuned to
// stay visible in both modes: a light tint of #1A524E on the white light menu, a brighter teal
// glow on the dark menu (where a low-opacity dark green would wash out).
const optionHighlight = theme => {
	const dark = theme.palette.mode === 'dark';
	return {
		hover: dark ? 'rgba(79, 209, 176, 0.12)' : 'rgba(26, 82, 78, 0.08)',
		selected: dark ? 'rgba(79, 209, 176, 0.2)' : 'rgba(26, 82, 78, 0.14)',
		selectedHover: dark ? 'rgba(79, 209, 176, 0.28)' : 'rgba(26, 82, 78, 0.2)'
	};
};

const components = {
	MuiButton: {
		styleOverrides: {
			root: {
				textTransform: 'none',
				fontWeight: 600,
				fontFamily: 'var(--font-source-sans-3)'
			}
		}
	},
	MuiTabs: {
		styleOverrides: {
			indicator: {
				backgroundColor: '#01584f'
			}
		}
	},
	MuiTab: {
		styleOverrides: {
			root: {
				'&.Mui-selected': {
					color: '#01584f'
				}
			}
		}
	},
	MuiOutlinedInput: {
		styleOverrides: {
			root: {
				'&.Mui-focused .MuiOutlinedInput-notchedOutline': {
					borderColor: '#01584f'
				}
			}
		}
	},
	MuiInputLabel: {
		styleOverrides: {
			root: {
				'&.Mui-focused': {
					color: '#01584f'
				}
			}
		}
	},
	// Select dropdown options (and any Menu using MenuItem).
	MuiMenuItem: {
		styleOverrides: {
			root: ({ theme }) => {
				const c = optionHighlight(theme);
				return {
					'&:hover': { backgroundColor: c.hover },
					'&.Mui-selected': { backgroundColor: c.selected },
					'&.Mui-selected:hover': { backgroundColor: c.selectedHover }
				};
			}
		}
	},
	// Autocomplete options use their own slot + aria-selected/focused states.
	MuiAutocomplete: {
		styleOverrides: {
			option: ({ theme }) => {
				const c = optionHighlight(theme);
				return {
					'&:hover': { backgroundColor: c.hover },
					'&.Mui-focused': { backgroundColor: c.hover },
					'&[aria-selected="true"]': { backgroundColor: c.selected },
					'&[aria-selected="true"]:hover': { backgroundColor: c.selectedHover },
					'&[aria-selected="true"].Mui-focused': { backgroundColor: c.selectedHover }
				};
			}
		}
	}
};

// Used only for augmentColor utility
const _defaultTheme = createTheme({});

export const polymerTheme = createTheme({
	spacing: 4,
	typography,
	palette: {
		text: { primary: '#2f3640' },
		background: { default: '#f2f9fc', paper: '#ffffff' },
		brand: BRAND,
		lumoraGreenPrimary: _defaultTheme.palette.augmentColor({
			color: { main: '#01584F' },
			name: 'lumoraGreenPrimary'
		})
	},
	components
});

export const polymerThemeDark = createTheme({
	spacing: 4,
	typography,
	palette: {
		mode: 'dark',
		text: { primary: '#ffffff', secondary: '#ffffff' },
		background: { default: 'hsl(220, 25%, 11%)', paper: 'hsl(220, 20%, 15%)' },
		divider: 'rgba(255,255,255,0.1)',
		brand: BRAND,
		lumoraGreenPrimary: _defaultTheme.palette.augmentColor({
			color: { main: '#01584F' },
			name: 'lumoraGreenPrimary'
		})
	},
	components
});

const ClientThemeProvider = ({ children }) => {
	const isDark = useThemeStore(state => state.isDark);
	const activeTheme = useMemo(() => (isDark ? polymerThemeDark : polymerTheme), [isDark]);

	// The persisted theme is applied after mount (skipHydration) so the server-rendered light
	// markup matches the first client render — a persist-driven dark initial state would be a
	// hydration mismatch (console error + flash) for returning dark-mode users.
	useEffect(() => {
		useThemeStore.persist.rehydrate();
	}, []);

	useEffect(() => {
		document.documentElement.dataset.colorScheme = isDark ? 'dark' : 'light';
	}, [isDark]);

	return (
		<AppRouterCacheProvider>
			<ThemeProvider theme={activeTheme}>
				<CssBaseline />
				{children}
			</ThemeProvider>
		</AppRouterCacheProvider>
	);
};

ClientThemeProvider.propTypes = {
	children: PropTypes.node.isRequired
};

export default ClientThemeProvider;
